package com.trevorwiebe.timesheet.more.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.appVersion
import com.trevorwiebe.timesheet.core.domain.model.UserRole
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.SectionLabel
import com.trevorwiebe.timesheet.core.presentation.common.botPadding
import com.trevorwiebe.timesheet.core.presentation.common.isSideNavVisible
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun MoreScreen(
    viewModel: MoreViewModel = koinViewModel(),
    onNavigateToPayPeriods: () -> Unit,
    onNavigateToTimeOff: () -> Unit,
    onNavigateToAdmin: () -> Unit,
    onNavigateToInvoicing: () -> Unit,
    onNavigateToSettlements: () -> Unit,
    onSignOut: () -> Unit
) {

    val state by viewModel.state.collectAsState()
    val scrollState = rememberScrollState()

    LaunchedEffect(viewModel.onSignOutChannel) {
        viewModel.onSignOutChannel.collect {
            onSignOut()
        }
    }

    Scaffold(
        topBar = { TopBar("More") }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
        Column(
            modifier = Modifier
                .verticalScroll(scrollState)
                .fillMaxSize()
        ) {
            val user = state.currentUser
            SectionLabel("Profile")
            if (user == null) {
                when {
                    state.userLoading -> ItemRow(
                        primaryText = "Loading your profile…"
                    )

                    state.userLoadFailed -> ItemRow(
                        primaryText = "Couldn't load your profile",
                        secondaryText = "Tap to retry",
                        onClick = { viewModel.onEvent(MoreEvents.OnRetryLoadUser) },
                        clickable = true
                    )

                    else -> ItemRow(
                        primaryText = "Profile",
                        secondaryText = "Unavailable"
                    )
                }
            } else {
                ItemRow(
                    primaryText = listOf(user.name, user.nickname)
                        .filter { !it.isNullOrEmpty() }
                        .joinToString(" - "),
                    secondaryText = user.email.takeIf { it.isNotEmpty() } ?: "Unavailable"
                )
                ItemRow(
                    primaryText = "Hire Date",
                    secondaryText = Util.toFriendlyDate(user.hireDate)
                )
            }
            ItemRow(
                primaryText = "Total Hours worked this year",
                secondaryAnnotatedText = buildAnnotatedString {
                    state.hoursWorked.forEachIndexed { index, (label, value) ->
                        withStyle(style = androidx.compose.ui.text.SpanStyle(
                            fontWeight = FontWeight.Bold
                        )) {
                            append(label)
                        }
                        append(": $value")
                        if (index != state.hoursWorked.lastIndex) {
                            append(" - ")
                        }
                    }
                }
            )
            if (user != null) {
                ItemRow(
                    primaryText = "Current PTO Balance",
                    secondaryText = if (user.ptoBalanceDays == 1.0)
                        "${user.ptoBalanceDays} day"
                    else "${user.ptoBalanceDays} days"
                )
            }
            // These actions live in the side rail/drawer on larger windows.
            if (!isSideNavVisible()) {
                SectionLabel("Actions")
                ItemRow(
                    primaryText = "Pay Periods",
                    onClick = onNavigateToPayPeriods,
                    clickable = true
                )
                ItemRow(
                    primaryText = "Time Off",
                    onClick = onNavigateToTimeOff,
                    clickable = true
                )
                if (state.currentUser?.role == UserRole.ADMIN) {
                    ItemRow(
                        primaryText = "Invoicing",
                        secondaryText = "Bill customers for delivered loads",
                        onClick = onNavigateToInvoicing,
                        clickable = true
                    )
                    ItemRow(
                        primaryText = "Settlements",
                        secondaryText = "Pay owner operators for their loads",
                        onClick = onNavigateToSettlements,
                        clickable = true
                    )
                    ItemRow(
                        primaryText = "Admin",
                        secondaryText = "Management functions available to admin users",
                        onClick = onNavigateToAdmin,
                        clickable = true
                    )
                }
                ItemRow(
                    primaryText = "Sign Out",
                    onClick = { viewModel.onEvent(MoreEvents.OnShowConfirmSignOutSheet(true)) },
                    clickable = true
                )
            }
            val version = appVersion()
            if (version.isNotEmpty()) {
                Text(
                    text = "Version $version",
                    fontSize = 12.sp,
                    color = MaterialTheme.colorScheme.secondary,
                    textAlign = TextAlign.Center,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 24.dp)
                )
            }
            Spacer(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(botPadding())
            )
        }
        }

        NativeDialog(
            visible = state.confirmSignOutSheet,
            title = "Sign Out",
            message = "Are you sure you want to sign out?",
            onDismiss = { viewModel.onEvent(MoreEvents.OnShowConfirmSignOutSheet(false)) },
            dismissText = "Cancel",
            onConfirm = { viewModel.onEvent(MoreEvents.OnSignOut) },
            confirmText = "Sign Out"
        )
    }
}

@Composable
private fun ItemRow(
    primaryText: String,
    secondaryText: String? = null,
    secondaryAnnotatedText: AnnotatedString? = null,
    clickable: Boolean = false,
    onClick: () -> Unit = { },
) {
    Column(
        modifier = if (clickable) {
            Modifier.fillMaxWidth().clickable { onClick() }
        } else {
            Modifier.fillMaxWidth()
        }
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    start = 16.dp,
                    end = 16.dp,
                    top = 16.dp,
                    bottom = if (secondaryText == null && secondaryAnnotatedText == null) 16.dp else 0.dp
                )
        ) {
            Text(
                fontSize = 16.sp,
                text = primaryText,
                fontWeight = FontWeight.SemiBold
            )
        }
        if (secondaryText != null) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = 16.dp, end = 16.dp, bottom = 16.dp)
            ) {
                Text(
                    fontSize = 14.sp,
                    text = secondaryText,
                    color = MaterialTheme.colorScheme.secondary
                )
            }
        }
        if (secondaryAnnotatedText != null) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = 16.dp, end = 16.dp, bottom = 16.dp)
            ) {
                Text(
                    fontSize = 14.sp,
                    text = secondaryAnnotatedText,
                    color = MaterialTheme.colorScheme.secondary
                )
            }
        }
        HorizontalDivider()
    }
}